/**
 * Ghost CMS Integration Landing Page
 *
 * SEO targets: "ghost cms ai content", "ghost blog automation",
 *              "ai writing tool for ghost"
 *
 * Public page — no auth required.
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Zap,
  Mail,
  Lock,
  Calendar,
  CheckCircle,
  ArrowRight,
  Globe,
  Users,
  BarChart3,
} from 'lucide-react';
import { ContentSageIcon } from '../components/ContentSageLogo';

// ---------------------------------------------------------------------------
// SEO helper — updates document head for this page
// ---------------------------------------------------------------------------

function GhostIntegrationSEO() {
  useEffect(() => {
    const prev = document.title;
    document.title =
      'ContentSage for Ghost CMS — AI Blog Automation & Ghost Blog Content Generator';

    // Meta description
    let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    const prevDesc = metaDesc.content;
    metaDesc.content =
      'Automate your Ghost blog with AI-generated content. ContentSage connects to Ghost CMS to publish SEO-optimised articles, newsletters, and member-only posts — hands-free.';

    // Keywords meta
    let metaKw = document.querySelector<HTMLMetaElement>('meta[name="keywords"]');
    if (!metaKw) {
      metaKw = document.createElement('meta');
      metaKw.name = 'keywords';
      document.head.appendChild(metaKw);
    }
    const prevKw = metaKw.content;
    metaKw.content =
      'ghost cms ai content, ghost blog automation, ai writing tool for ghost, ghost cms content generator, automate ghost blog, ghost publishing ai';

    return () => {
      document.title = prev;
      if (metaDesc) metaDesc.content = prevDesc;
      if (metaKw) metaKw.content = prevKw;
    };
  }, []);

  return null;
}

// ---------------------------------------------------------------------------
// Setup steps data
// ---------------------------------------------------------------------------

const SETUP_STEPS = [
  {
    number: 1,
    title: 'Add a Custom Integration in Ghost Admin',
    description:
      'In your Ghost Admin dashboard, go to Settings → Integrations → Add Custom Integration.',
    screenshot: '/screenshots/ghost-step1-integrations.png',
    screenshotAlt: 'Ghost Admin Settings > Integrations page',
  },
  {
    number: 2,
    title: 'Name it "ContentSage" and copy the Admin API Key',
    description:
      'Give the integration any name (e.g. "ContentSage"), then copy the Admin API Key that Ghost generates.',
    screenshot: '/screenshots/ghost-step2-api-key.png',
    screenshotAlt: 'Ghost custom integration with Admin API Key highlighted',
  },
  {
    number: 3,
    title: 'Open ContentSage → Settings → Platform Connections',
    description:
      'In your ContentSage dashboard, navigate to Settings → Platform Connections and click "Connect Ghost".',
    screenshot: '/screenshots/ghost-step3-contentsage-settings.png',
    screenshotAlt: 'ContentSage Platform Connections settings panel',
  },
  {
    number: 4,
    title: 'Paste your Ghost URL and Admin API Key',
    description:
      'Enter your Ghost site URL (e.g. https://myblog.ghost.io) and paste the Admin API Key from Step 2.',
    screenshot: '/screenshots/ghost-step4-connect-form.png',
    screenshotAlt: 'ContentSage Ghost connection form',
  },
  {
    number: 5,
    title: 'Start generating and publishing',
    description:
      'Your Ghost site is now connected. ContentSage will publish AI-generated articles directly to Ghost as drafts or live posts.',
    screenshot: '/screenshots/ghost-step5-publishing.png',
    screenshotAlt: 'ContentSage publishing dashboard with Ghost connected',
  },
];

// ---------------------------------------------------------------------------
// Feature highlights specific to Ghost
// ---------------------------------------------------------------------------

const GHOST_FEATURES = [
  {
    icon: Mail,
    title: 'Newsletter-ready content',
    description:
      "ContentSage generates articles formatted for Ghost's built-in newsletter feature — so your subscribers get fresh content automatically.",
  },
  {
    icon: Lock,
    title: 'Member-only posts',
    description:
      "Publish paid or free member-only content on a schedule. ContentSage respects Ghost's membership tiers and access levels.",
  },
  {
    icon: Calendar,
    title: 'Scheduled publishing',
    description:
      'Queue up weeks of content in advance. ContentSage publishes posts at your chosen dates and times via the Ghost Admin API.',
  },
  {
    icon: BarChart3,
    title: 'SEO-optimised by default',
    description:
      'Every article includes meta title, meta description, and Ghost-native SEO fields — no plugin required.',
  },
  {
    icon: Globe,
    title: 'Works with self-hosted Ghost',
    description:
      "Whether you're on Ghost.org or self-hosting, ContentSage connects via the standard Admin API — no server access needed.",
  },
  {
    icon: Users,
    title: 'Multi-site support',
    description:
      'Manage content for multiple Ghost sites from a single ContentSage account.',
  },
];

// ---------------------------------------------------------------------------
// Testimonial placeholders
// ---------------------------------------------------------------------------

const TESTIMONIALS = [
  {
    quote:
      '"We went from publishing once a week to five times a week on our Ghost blog — without hiring extra writers. ContentSage handles the research and drafting; we just review and hit publish."',
    author: 'Sarah T.',
    role: 'Founder, TechReview Weekly',
    avatar: null,
  },
  {
    quote:
      '"The Ghost integration took about two minutes to set up. Now our newsletter audience gets consistent content even during our busy months."',
    author: 'Marcus L.',
    role: 'Content Strategist',
    avatar: null,
  },
  {
    quote:
      "\"Finally — an AI tool that understands Ghost's member model. Paid posts, free posts, newsletters — ContentSage handles all of it.\"",
    author: 'Priya M.',
    role: 'Creator & Ghost publisher',
    avatar: null,
  },
];

// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 },
  }),
};

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function GhostIntegrationPage() {
  return (
    <>
      <GhostIntegrationSEO />

      <div className="min-h-screen bg-white text-gray-900">
        {/* ------------------------------------------------------------------ */}
        {/* Nav */}
        {/* ------------------------------------------------------------------ */}
        <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ContentSageIcon size={32} />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-amber-500 bg-clip-text text-transparent">
                ContentSage
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link
                to="/login"
                className="text-sm text-gray-600 hover:text-gray-900 font-medium transition"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                className="text-sm bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-purple-700 transition"
              >
                Start free
              </Link>
            </div>
          </div>
        </nav>

        {/* ------------------------------------------------------------------ */}
        {/* Hero */}
        {/* ------------------------------------------------------------------ */}
        <section className="bg-gradient-to-br from-purple-50 via-white to-amber-50 py-20 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 text-sm font-medium px-4 py-1.5 rounded-full mb-6"
            >
              <Zap className="w-4 h-4" />
              Native Ghost CMS Integration
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6"
            >
              ContentSage for{' '}
              <span className="bg-gradient-to-r from-purple-600 to-amber-500 bg-clip-text text-transparent">
                Ghost CMS
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-xl text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed"
            >
              AI-generated blog posts, newsletters, and member-only content —
              published directly to your Ghost site. No copy-paste. No plugins.
              Just results.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 bg-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-purple-700 transition shadow-lg shadow-purple-200"
              >
                Start Free — No Credit Card Required
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="#setup"
                className="inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-700 px-8 py-4 rounded-xl text-lg font-medium hover:border-gray-400 transition"
              >
                See how it works
              </a>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="mt-10 flex flex-wrap justify-center gap-6 text-sm text-gray-500"
            >
              {['2-minute setup', 'Works with Ghost.org & self-hosted', 'Cancel anytime'].map(
                (badge) => (
                  <span key={badge} className="flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {badge}
                  </span>
                )
              )}
            </motion.div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* Step-by-step setup guide */}
        {/* ------------------------------------------------------------------ */}
        <section id="setup" className="py-20 px-4 sm:px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Connect Ghost in 5 minutes
              </h2>
              <p className="text-lg text-gray-500 max-w-xl mx-auto">
                No coding. No plugins. Just your Ghost Admin API key.
              </p>
            </div>

            <div className="space-y-12">
              {SETUP_STEPS.map((step, i) => (
                <motion.div
                  key={step.number}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeUp}
                  className="flex flex-col md:flex-row gap-8 items-start"
                >
                  {/* Step number */}
                  <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-purple-600 text-white text-xl font-bold shadow-md">
                    {step.number}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {step.description}
                    </p>

                    {/* Screenshot placeholder */}
                    <div className="bg-gray-100 rounded-xl overflow-hidden border border-gray-200 aspect-video max-w-lg flex items-center justify-center">
                      <img
                        src={step.screenshot}
                        alt={step.screenshotAlt}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = `<span class="text-gray-400 text-sm px-8 text-center">${step.screenshotAlt}</span>`;
                          }
                        }}
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* Ghost-specific feature highlights */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-20 px-4 sm:px-6 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Built for Ghost publishers
              </h2>
              <p className="text-lg text-gray-500 max-w-xl mx-auto">
                ContentSage understands what makes Ghost different — and uses it
                to your advantage.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {GHOST_FEATURES.map((feature, i) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={feature.title}
                    custom={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    variants={fadeUp}
                    className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition"
                  >
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* Testimonials */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-20 px-4 sm:px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Ghost publishers love ContentSage
              </h2>
            </div>

            <div className="grid sm:grid-cols-3 gap-6">
              {TESTIMONIALS.map((t, i) => (
                <motion.div
                  key={t.author}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fadeUp}
                  className="bg-gray-50 rounded-2xl p-6 border border-gray-100"
                >
                  <p className="text-gray-700 text-sm leading-relaxed italic mb-5">
                    {t.quote}
                  </p>
                  <div className="flex items-center gap-3">
                    {/* Avatar placeholder */}
                    <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-sm flex-shrink-0">
                      {t.author.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {t.author}
                      </p>
                      <p className="text-xs text-gray-500">{t.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* CTA */}
        {/* ------------------------------------------------------------------ */}
        <section className="py-20 px-4 sm:px-6 bg-gradient-to-br from-purple-600 to-purple-800 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-3xl sm:text-4xl font-extrabold mb-4"
            >
              Ready to automate your Ghost blog?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="text-purple-200 text-lg mb-10 max-w-xl mx-auto"
            >
              Start for free. Connect your Ghost site in minutes. Your first 2
              posts are on us.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-white text-purple-700 px-10 py-4 rounded-xl text-lg font-bold hover:bg-purple-50 transition shadow-xl"
              >
                Start Free — No Credit Card Required
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* Footer */}
        {/* ------------------------------------------------------------------ */}
        <footer className="py-8 px-4 sm:px-6 border-t border-gray-100 text-center text-sm text-gray-500">
          <div className="flex flex-wrap justify-center gap-4 mb-3">
            <Link to="/terms" className="hover:text-gray-700 transition">Terms</Link>
            <Link to="/privacy" className="hover:text-gray-700 transition">Privacy</Link>
            <Link to="/pricing" className="hover:text-gray-700 transition">Pricing</Link>
          </div>
          <p>© {new Date().getFullYear()} ContentSage. All rights reserved.</p>
        </footer>
      </div>
    </>
  );
}
